import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeft, Search, Settings, RefreshCw, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext.tsx';
import { MatchCard, TradeListManager } from '../components/exchange/index.ts';
import {
  getExchangeProfile,
  findTradeMatches,
  syncForTradeCards,
  initializeExchangeProfile
} from '../lib/exchangeService.ts';
import { ExchangeProfile, TradeMatch } from '../types/collection.ts';
import { getProfile } from '../lib/profile.ts';

type Tab = 'matches' | 'forTrade' | 'seeking' | 'settings';

export default function Exchange() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>('matches');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [profile, setProfile] = useState<ExchangeProfile | null>(null);
  const [matches, setMatches] = useState<TradeMatch[]>([]);
  const [mutualOnly, setMutualOnly] = useState(true);

  const loadData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Get or create exchange profile
      let exchangeProfile = await getExchangeProfile(user.uid);

      if (!exchangeProfile) {
        // Initialize profile
        const userProfile = await getProfile(user.uid);
        await initializeExchangeProfile(
          user.uid,
          userProfile?.handle || user.displayName || 'user'
        );
        exchangeProfile = await getExchangeProfile(user.uid);
      }

      setProfile(exchangeProfile);

      // Find matches
      const tradeMatches = await findTradeMatches(user.uid, { mutualOnly });
      setMatches(tradeMatches);
    } catch (error) {
      console.error('Failed to load exchange data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, mutualOnly]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    if (!user) return;

    setRefreshing(true);
    try {
      // Sync for-trade cards
      await syncForTradeCards(user.uid);
      // Reload data
      await loadData();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleViewProfile = (uid: string) => {
    navigate(`/profile/${uid}`);
  };

  const handleContact = (match: TradeMatch) => {
    // For now, just navigate to profile
    // In V2, this would open in-app messaging
    navigate(`/profile/${match.user.uid}`);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <ArrowRightLeft className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Sign in to trade</h2>
          <p className="text-gray-400">You need to be signed in to use the exchange</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'matches' as Tab, label: 'Find Trades', icon: Users },
    { id: 'forTrade' as Tab, label: 'For Trade', icon: ArrowRightLeft },
    { id: 'seeking' as Tab, label: 'Seeking', icon: Search },
    { id: 'settings' as Tab, label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-900/80 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="text-xl font-bold text-white">Card Exchange</h1>
              <p className="text-sm text-gray-400">Find traders for your cards</p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 -mb-px">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors
                  ${activeTab === tab.id
                    ? 'text-white border-b-2 border-blue-500'
                    : 'text-gray-400 hover:text-white'
                  }
                `}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Matches Tab */}
            {activeTab === 'matches' && (
              <div className="space-y-4">
                {/* Filter */}
                <div className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3">
                  <span className="text-sm text-gray-300">Show only mutual matches</span>
                  <button
                    onClick={() => setMutualOnly(!mutualOnly)}
                    className={`
                      relative w-11 h-6 rounded-full transition-colors
                      ${mutualOnly ? 'bg-blue-600' : 'bg-gray-700'}
                    `}
                  >
                    <span
                      className={`
                        absolute top-1 w-4 h-4 rounded-full bg-white transition-transform
                        ${mutualOnly ? 'left-6' : 'left-1'}
                      `}
                    />
                  </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-white">
                      {profile?.forTrade?.length || 0}
                    </p>
                    <p className="text-sm text-gray-400">Cards for trade</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-white">
                      {profile?.seeking?.length || 0}
                    </p>
                    <p className="text-sm text-gray-400">Cards seeking</p>
                  </div>
                </div>

                {/* Matches list */}
                {matches.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No matches found</h3>
                    <p className="text-gray-400 mb-4">
                      {profile?.seeking?.length === 0
                        ? 'Add cards to your seeking list to find traders'
                        : 'No one currently has the cards you\'re looking for'}
                    </p>
                    <button
                      onClick={() => setActiveTab('seeking')}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                    >
                      Update Seeking List
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-400">
                      Found {matches.length} potential trade{matches.length !== 1 ? 's' : ''}
                    </p>
                    {matches.map(match => (
                      <MatchCard
                        key={match.user.uid}
                        match={match}
                        onViewProfile={handleViewProfile}
                        onContact={handleContact}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* For Trade Tab */}
            {activeTab === 'forTrade' && (
              <div className="bg-gray-800/30 rounded-xl p-4">
                <h2 className="text-lg font-semibold text-white mb-4">
                  Manage Cards for Trade
                </h2>
                <TradeListManager
                  userId={user.uid}
                  mode="forTrade"
                  onUpdate={loadData}
                />
              </div>
            )}

            {/* Seeking Tab */}
            {activeTab === 'seeking' && (
              <div className="bg-gray-800/30 rounded-xl p-4">
                <h2 className="text-lg font-semibold text-white mb-4">
                  Cards You're Seeking
                </h2>
                <TradeListManager
                  userId={user.uid}
                  mode="seeking"
                  seekingList={profile?.seeking || []}
                  onUpdate={loadData}
                />
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="bg-gray-800/30 rounded-xl p-6 space-y-6">
                <h2 className="text-lg font-semibold text-white">
                  Exchange Settings
                </h2>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">Shipping Available</p>
                      <p className="text-sm text-gray-400">
                        Are you willing to ship cards?
                      </p>
                    </div>
                    <button
                      className={`
                        relative w-11 h-6 rounded-full transition-colors
                        ${profile?.preferences?.shippingAvailable ? 'bg-blue-600' : 'bg-gray-700'}
                      `}
                    >
                      <span
                        className={`
                          absolute top-1 w-4 h-4 rounded-full bg-white transition-transform
                          ${profile?.preferences?.shippingAvailable ? 'left-6' : 'left-1'}
                        `}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">Local Trades Only</p>
                      <p className="text-sm text-gray-400">
                        Only show matches in your area
                      </p>
                    </div>
                    <button
                      className={`
                        relative w-11 h-6 rounded-full transition-colors
                        ${profile?.preferences?.localOnly ? 'bg-blue-600' : 'bg-gray-700'}
                      `}
                    >
                      <span
                        className={`
                          absolute top-1 w-4 h-4 rounded-full bg-white transition-transform
                          ${profile?.preferences?.localOnly ? 'left-6' : 'left-1'}
                        `}
                      />
                    </button>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-700">
                  <p className="text-sm text-gray-500">
                    Your exchange profile is visible to other traders when you have cards for trade.
                    Contact information from your main profile will be shared with matched traders.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
